import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

import { getAnalyticsAdapter } from "../analytics/AnalyticsAdapter";
import { getSessionTracker } from "../analytics/session";
import { analytics } from "../analytics/analyticsEvents";
import { resetAnonId } from "../analytics/anonId";
import { getPlatformAdapter } from "../platform/PlatformAdapter";
import { clients, contracts, getContractById, skills as allSkills, tools } from "../content";
import { refreshOffers, isUrgentActive } from "../engine/contractOffers";
import { applyContractRewards } from "../engine/progression";
import type { ContractRewardSummary } from "../engine/progression";
import { checkAchievements } from "../engine/achievements";
import { applySkillUpgrade, isSkillUnlocked } from "../engine/skills";
import { applyTraining } from "../engine/training";
import { applyToolPurchase } from "../engine/tools";
import type { ContractId, ContractType, Player, SkillId, ToolId } from "../types";
import { SKILL_IDS } from "../types/enums";
import { SAVE_VERSION, type ResultHistoryEntry, type SaveGame } from "../types/player";
import { createDefaultSaveGame } from "./defaultSave";
import { migrateSave, parseSaveGame } from "./parseSave";
import { createSafeStorage } from "./safeStorage";
import type { Screen } from "./screen";
import { readStoredLanguage, storeLanguage } from "./languagePreference";
import { DEFAULT_SETTINGS, type Language, type Settings } from "./settings";
import { useTutorialStore } from "./tutorialStore";

export interface ContractResult {
  contractId: ContractId;
  summary: ContractRewardSummary;
  newAchievements: string[];
  taskScores: number[];
  taskTypes?: ContractType[];
  readOnly: boolean;
}

interface RuntimeState {
  currentScreen: Screen;
  inGame: boolean;
  activeContractId: ContractId | null;
  activeToolId: ToolId | null;
  activeSkillId: SkillId | null;
  activeTaskIndex: number;
  currentTaskResults: number[];
  lastResult: ContractResult | null;
  settings: Settings;
  activeToolIds: ToolId[];
  activeSkillIds: SkillId[];
}

interface Actions {
  newGame: (options?: { playerName?: string; agencyName?: string }) => void;
  continueGame: () => void;
  resetGame: () => void;
  navigateTo: (screen: Screen) => void;
  setActiveContract: (contractId: ContractId | null) => void;
  setActiveTool: (toolId: ToolId | null) => void;
  setActiveSkill: (skillId: SkillId | null) => void;
  setLanguage: (language: Language) => void;
  /** Applies an auto-detected language without recording it as the player's choice. */
  applyDetectedLanguage: (language: Language) => void;
  completeTask: (score: number) => void;
  completeContract: (taskScores?: readonly number[]) => void;
  viewHistoryResult: (entry: ResultHistoryEntry) => void;
  refreshOffersNow: () => void;
  upgradeSkill: (skillId: SkillId) => void;
  buyTraining: () => void;
  purchaseTool: (toolId: ToolId) => void;
  toggleActiveTool: (toolId: ToolId) => void;
  toggleActiveSkill: (skillId: SkillId) => void;
}

export type GameState = SaveGame & RuntimeState & Actions;

function createRuntimeState(): RuntimeState {
  return {
    currentScreen: "main_menu",
    inGame: false,
    activeContractId: null,
    activeToolId: null,
    activeSkillId: null,
    activeTaskIndex: 0,
    currentTaskResults: [],
    lastResult: null,
    settings: { ...DEFAULT_SETTINGS, language: readStoredLanguage() ?? DEFAULT_SETTINGS.language },
    activeToolIds: [],
    activeSkillIds: [],
  };
}

function learnedSkillIds(skills: Record<SkillId, number>): SkillId[] {
  return SKILL_IDS.filter((id) => (skills[id] ?? 0) > 0);
}

function withId<T extends string>(ids: readonly T[], id: T): T[] {
  return ids.includes(id) ? [...ids] : [...ids, id];
}

/** Projection persisted by the middleware: progress only, no runtime-UI. */
export function partializeSave(state: GameState): SaveGame {
  return {
    saveVersion: SAVE_VERSION,
    player: state.player,
    agency: state.agency,
    clientTrust: state.clientTrust,
    unlockedClients: state.unlockedClients,
    streak: state.streak,
    offers: state.offers,
    lastSaveTime: state.lastSaveTime,
    resultHistory: state.resultHistory,
    training: state.training,
  };
}

export const useGameStore = create<GameState>()(
  persist<GameState, [], [], SaveGame>(
    (set, get) => ({
      ...createDefaultSaveGame(),
      ...createRuntimeState(),

      newGame: (options) => {
        set((state) => {
          const fresh = createDefaultSaveGame();
          return {
            ...fresh,
            player: { ...fresh.player, name: options?.playerName ?? fresh.player.name },
            agency: { ...fresh.agency, name: options?.agencyName ?? fresh.agency.name },
            lastSaveTime: Date.now(),
            currentScreen: "dashboard",
            inGame: true,
            activeContractId: null,
            activeToolId: null,
            activeSkillId: null,
            activeTaskIndex: 0,
            currentTaskResults: [],
            lastResult: null,
            settings: state.settings,
            activeToolIds: [],
            activeSkillIds: [],
          };
        });
        get().refreshOffersNow();
      },

      continueGame: () => {
        set(() => ({ currentScreen: "dashboard", inGame: true }));
        get().refreshOffersNow();
      },

      resetGame: () => {
        set((state) => ({
          ...createDefaultSaveGame(),
          currentScreen: "main_menu",
          inGame: false,
          activeContractId: null,
          activeToolId: null,
          activeSkillId: null,
          activeTaskIndex: 0,
          currentTaskResults: [],
          lastResult: null,
          settings: state.settings,
          activeToolIds: [],
          activeSkillIds: [],
        }));
        useTutorialStore.getState().reset();
        const analyticsAdapter = getAnalyticsAdapter();
        analyticsAdapter.reset();
        analyticsAdapter.identify(resetAnonId());
      },

      navigateTo: (screen) => set(() => ({ currentScreen: screen })),

      setActiveContract: (contractId) =>
        set((state) => ({
          activeContractId: contractId,
          activeTaskIndex: 0,
          currentTaskResults: [],
          activeToolIds: contractId ? [...state.player.ownedTools] : [],
          activeSkillIds: contractId ? learnedSkillIds(state.player.skills) : [],
        })),

      setActiveTool: (toolId) => set(() => ({ activeToolId: toolId })),

      setActiveSkill: (skillId) => set(() => ({ activeSkillId: skillId })),

      setLanguage: (language) => {
        set((state) => ({ settings: { ...state.settings, language } }));
        storeLanguage(language);
        analytics.languageChanged({ lang: language });
      },

      applyDetectedLanguage: (language) =>
        set((state) => ({ settings: { ...state.settings, language } })),

      completeTask: (score) =>
        set((state) => ({
          currentTaskResults: [...state.currentTaskResults, score],
          activeTaskIndex: state.activeTaskIndex + 1,
        })),

      completeContract: (taskScores) => {
        const state = get();
        const contract = state.activeContractId
          ? getContractById(state.activeContractId)
          : undefined;
        if (!contract) {
          return;
        }
        const scores = taskScores ?? state.currentTaskResults;
        const urgentBonusPct = isUrgentActive(state.offers, contract.id, Date.now())
          ? state.offers.urgent!.bonusPct
          : 0;
        const { save, summary } = applyContractRewards(
          partializeSave(state),
          contract,
          scores,
          clients,
          urgentBonusPct,
        );

        const reportIndex = contract.tasks.indexOf("report_builder");
        const lastReportScore = reportIndex >= 0 ? scores[reportIndex] : undefined;
        const newAchievements = checkAchievements({
          save,
          clients,
          lastGrade: summary.grade,
          lastReportScore,
        });
        const player: Player =
          newAchievements.length > 0
            ? { ...save.player, achievements: [...save.player.achievements, ...newAchievements] }
            : save.player;

        const historyEntry: ResultHistoryEntry = {
          contractId: contract.id,
          grade: summary.grade,
          score: summary.score,
          money: summary.reward.money,
          xp: summary.reward.xp,
          at: Date.now(),
          taskScores: [...scores],
          taskTypes: [...contract.tasks],
        };

        set({
          player,
          agency: save.agency,
          clientTrust: save.clientTrust,
          unlockedClients: save.unlockedClients,
          streak: save.streak,
          offers: refreshOffers(save.offers, {
            contracts,
            player: save.player,
            now: Date.now(),
            rng: Math.random,
          }),
          resultHistory: [historyEntry, ...state.resultHistory].slice(0, 20),
          lastResult: {
            contractId: contract.id,
            summary,
            newAchievements,
            taskScores: [...scores],
            taskTypes: [...contract.tasks],
            readOnly: false,
          },
          lastSaveTime: Date.now(),
          currentScreen: "results",
        });

        getAnalyticsAdapter().track("contract_completed", {
          contractId: contract.id,
          grade: summary.grade,
          score: summary.score,
        });
        getSessionTracker()?.noteContractCompleted();
        if (summary.leveledUp) {
          analytics.levelUp({ level: summary.newLevel });
        }
        for (const clientId of save.unlockedClients) {
          if (!state.unlockedClients.includes(clientId)) {
            analytics.clientUnlocked({ clientId });
          }
        }
        for (const achievementId of newAchievements) {
          analytics.achievementUnlocked({ achievementId });
        }
        if (summary.grade !== "Failed") {
          getPlatformAdapter().happyTime();
        }
      },

      viewHistoryResult: (entry) =>
        set((state) => ({
          lastResult: {
            contractId: entry.contractId,
            summary: {
              score: entry.score,
              grade: entry.grade,
              reward: { money: entry.money, xp: entry.xp, reputation: 0 },
              previousLevel: state.player.level,
              newLevel: state.player.level,
              leveledUp: false,
              skillPointsGained: 0,
              urgentBonusPct: 0,
              trustBonusPct: 0,
              replay: false,
            },
            newAchievements: [],
            taskScores: [...(entry.taskScores ?? [])],
            taskTypes: entry.taskTypes ? [...entry.taskTypes] : undefined,
            readOnly: true,
          },
          currentScreen: "results",
        })),

      upgradeSkill: (skillId) => {
        const state = get();
        const skill = allSkills.find((s) => s.id === skillId);
        if (skill && !isSkillUnlocked(skill.minLevel, state.player.level)) {
          return;
        }
        const player = applySkillUpgrade(state.player, skillId);
        if (player === state.player) {
          return;
        }
        set({ player, activeSkillIds: withId(state.activeSkillIds, skillId) });
        analytics.skillUpgraded({ skillId, level: player.skills[skillId] ?? 0 });
      },

      buyTraining: () => {
        const state = get();
        const player = applyTraining(state.player, state.training.bought);
        if (player === state.player) {
          return;
        }
        set({
          player,
          training: { bought: state.training.bought + 1 },
          lastSaveTime: Date.now(),
        });
        getAnalyticsAdapter().track("training_bought", { count: state.training.bought + 1 });
      },

      purchaseTool: (toolId) => {
        const state = get();
        const tool = tools.find((entry) => entry.id === toolId);
        if (!tool) {
          return;
        }
        const purchased = applyToolPurchase(state.player, tool);
        if (purchased === state.player) {
          return;
        }
        const newAchievements = checkAchievements({
          save: { ...partializeSave(state), player: purchased },
          clients,
        });
        const player: Player =
          newAchievements.length > 0
            ? { ...purchased, achievements: [...purchased.achievements, ...newAchievements] }
            : purchased;
        set({ player, activeToolIds: withId(state.activeToolIds, toolId) });
        analytics.toolPurchased({ toolId, cost: tool.cost });
        for (const achievementId of newAchievements) {
          analytics.achievementUnlocked({ achievementId });
        }
      },

      refreshOffersNow: () =>
        set((state) => ({
          offers: refreshOffers(state.offers, {
            contracts,
            player: state.player,
            now: Date.now(),
            rng: Math.random,
          }),
        })),

      toggleActiveTool: (toolId) =>
        set((state) => ({
          activeToolIds: state.activeToolIds.includes(toolId)
            ? state.activeToolIds.filter((id) => id !== toolId)
            : [...state.activeToolIds, toolId],
        })),

      toggleActiveSkill: (skillId) =>
        set((state) => ({
          activeSkillIds: state.activeSkillIds.includes(skillId)
            ? state.activeSkillIds.filter((id) => id !== skillId)
            : [...state.activeSkillIds, skillId],
        })),
    }),
    {
      name: "breachguard-save",
      version: SAVE_VERSION,
      storage: createJSONStorage(() => createSafeStorage()),
      partialize: (state) => partializeSave(state),
      migrate: (persisted, version) => migrateSave(persisted, version),
      merge: (persisted, current) => ({
        ...current,
        ...(persisted as Partial<GameState>),
      }),
      onRehydrateStorage: () => (_state, error) => {
        if (error) {
          getAnalyticsAdapter().track("save_rehydrate_error");
        }
      },
    },
  ),
);

/** Service operations: thin wrappers over the persist layer. */
export function exportSave(): string {
  return JSON.stringify(partializeSave(useGameStore.getState()));
}

export function importSave(json: string): boolean {
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    return false;
  }
  const parsed = parseSaveGame(raw);
  if (!parsed) {
    return false;
  }
  useGameStore.setState(parsed);
  return true;
}
